import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { agencyInfo } from '../mock';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // More 3D geometric shapes
    const shapes = [];
    const shapeCount = 20;

    class GeometricShape3D {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 120 + 60;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.015;
        this.vx = (Math.random() - 0.5) * 0.4;
        this.vy = (Math.random() - 0.5) * 0.4;
        this.type = Math.floor(Math.random() * 4);
        this.depth = Math.random();
        this.scale = 0.5 + this.depth * 0.5;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.rotation += this.rotationSpeed;

        if (this.x < -this.size) this.x = canvas.width + this.size;
        if (this.x > canvas.width + this.size) this.x = -this.size;
        if (this.y < -this.size) this.y = canvas.height + this.size;
        if (this.y > canvas.height + this.size) this.y = -this.size;
      }

      draw() {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.scale(this.scale, this.scale);
        
        const alpha = 0.08 + this.depth * 0.12;
        ctx.strokeStyle = `rgba(0, 0, 0, ${alpha})`;
        ctx.fillStyle = `rgba(0, 0, 0, ${alpha * 0.3})`;
        ctx.lineWidth = 2 + this.depth * 2;
        ctx.beginPath();

        if (this.type === 0) {
          // Triangle with 3D effect
          const h = this.size / 2;
          ctx.moveTo(0, -h);
          ctx.lineTo(h, h);
          ctx.lineTo(-h, h);
          ctx.closePath();
        } else if (this.type === 1) {
          // Square with perspective
          const s = this.size / 2;
          ctx.rect(-s, -s, this.size, this.size);
        } else if (this.type === 2) {
          // Hexagon
          const sides = 6;
          for (let i = 0; i < sides; i++) {
            const angle = (Math.PI * 2 * i) / sides;
            const x = Math.cos(angle) * this.size / 2;
            const y = Math.sin(angle) * this.size / 2;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.closePath();
        } else {
          // Diamond
          const s = this.size / 2;
          ctx.moveTo(0, -s);
          ctx.lineTo(s, 0);
          ctx.lineTo(0, s);
          ctx.lineTo(-s, 0);
          ctx.closePath();
        }

        ctx.fill();
        ctx.stroke();
        
        // Add 3D depth lines
        ctx.strokeStyle = `rgba(0, 0, 0, ${alpha * 0.5})`;
        ctx.lineWidth = 1;
        if (this.type === 1) {
          const offset = 15 * this.depth;
          ctx.beginPath();
          const s = this.size / 2;
          ctx.moveTo(s, -s);
          ctx.lineTo(s + offset, -s - offset);
          ctx.moveTo(s, s);
          ctx.lineTo(s + offset, s - offset);
          ctx.stroke();
        }
        
        ctx.restore();
      }
    }

    for (let i = 0; i < shapeCount; i++) {
      shapes.push(new GeometricShape3D());
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      shapes.forEach(shape => {
        shape.update();
        shape.draw();
      });
      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-white">
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 lg:py-32 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left Content */}
        <div 
          className="space-y-8"
          style={{
            transform: `translateY(${scrollY * 0.2}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        >
          {/* Small badge */}
          <div className="inline-block">
            <div className="px-4 py-2 bg-black/5 border border-black/10 inline-flex items-center gap-2">
              <div className="w-2 h-2 bg-black rounded-full animate-pulse" />
              <span className="text-sm tracking-wider uppercase text-black/70">AI-Powered Marketing</span>
            </div>
          </div>

          {/* Main Headline */}
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight"
            style={{
              transform: `translateZ(30px)`,
              transformStyle: 'preserve-3d',
              textShadow: '2px 2px 0px rgba(0,0,0,0.03)'
            }}
          >
            <span className="text-black">
              AI Ad Creatives & Product Campaigns for Modern Brands
            </span>
          </h1>

          {/* Subheadline */}
          <p 
            className="text-base md:text-lg text-[#666666] leading-relaxed font-light max-w-xl"
            style={{
              transform: `translateZ(20px)`,
              transformStyle: 'preserve-3d'
            }}
          >
            Cortexa creates AI-powered product photoshoots, cinematic ads, AI model campaigns, and high-converting websites for brands.
          </p>

          {/* 2 CTA Buttons */}
          <div 
            className="flex flex-col sm:flex-row gap-4"
            style={{
              transform: `translateZ(10px)`,
              transformStyle: 'preserve-3d'
            }}
          >
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-black hover:bg-[#1a1a1a] text-white px-8 py-6 text-base font-medium transition-all duration-300 group"
              style={{
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
              }}
            >
              <span>Get Started</span>
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => navigate('/portfolio')}
              size="lg"
              variant="outline"
              className="border-2 border-black text-black hover:bg-black hover:text-white px-8 py-6 text-base font-medium transition-all duration-300 group"
            >
              <span>View Our Work</span>
            </Button>
          </div>
        </div>

        {/* Right - AI Visual */}
        <div 
          className="relative"
          style={{
            transform: `translateY(${scrollY * 0.1}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        >
          <div className="relative group">
            {/* Background glow */}
            <div className="absolute inset-0 bg-black/10 rounded-lg blur-3xl scale-110 group-hover:scale-125 transition-transform duration-700" />
            
            {/* Main image container */}
            <div 
              className="relative aspect-square overflow-hidden bg-black/5 border-2 border-black/10"
              style={{
                transform: 'perspective(1000px) rotateY(-5deg)',
                transformStyle: 'preserve-3d'
              }}
            >
              <img 
                src="https://images.unsplash.com/photo-1770034285743-c463411cd660?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBwcm9kdWN0JTIwcGhvdG9ncmFwaHklMjBzdHVkaW8lMjBtb2Rlcm58ZW58MHx8fHwxNzc1MTQwMDMxfDA&ixlib=rb-4.1.0&q=85"
                alt="AI Marketing Visual" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                style={{
                  transform: 'translateZ(20px)'
                }}
              />
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-tr from-black/20 via-transparent to-transparent" />
              
              {/* Floating badge */}
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-md px-4 py-3 border border-black/10">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-sm font-medium text-black">AI-Generated</span>
                </div>
              </div>
            </div>

            {/* Decorative 3D elements */}
            <div 
              className="absolute -top-6 -right-6 w-32 h-32 border-2 border-black/10 -z-10"
              style={{
                animation: 'float-shape 6s ease-in-out infinite'
              }}
            />
            <div 
              className="absolute -bottom-6 -left-6 w-24 h-24 bg-black/5 -z-10"
              style={{
                animation: 'float-shape 8s ease-in-out infinite reverse'
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float-shape {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </section>
  );
};

export default Hero;